import { forwardRef, useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import { Loader2, X } from 'lucide-react';
import { useGame } from '@/store/game';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';

const variants: Record<Variant, string> = {
  primary: 'bg-blood-600 text-white hover:bg-blood-500 shadow-lg shadow-blood-700/30',
  gold: 'bg-gold-400 text-ink-950 hover:bg-gold-300 shadow-lg shadow-gold-500/20',
  secondary: 'bg-ink-700 text-zinc-100 hover:bg-ink-600 border border-white/5',
  ghost: 'text-zinc-300 hover:bg-white/5 hover:text-white',
  danger: 'bg-transparent text-blood-400 border border-blood-600/40 hover:bg-blood-600/10',
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean }
>(function Button({ variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition active:scale-[.98] disabled:pointer-events-none disabled:opacity-40',
        size === 'sm' && 'px-3 py-1.5 text-sm',
        size === 'md' && 'px-4 py-2.5',
        size === 'lg' && 'px-6 py-3.5 text-lg',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { error?: string }>(
  function Input({ className, error, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={clsx('input', error && 'border-blood-500/70 focus:border-blood-500 focus:ring-blood-500/20', className)}
        {...rest}
      />
    );
  },
);

export function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-sm text-blood-400">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-zinc-500">{hint}</span>
      ) : null}
    </label>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={clsx('card', className)}>{children}</div>;
}

export function Badge({ tone = 'neutral', children, className }: { tone?: 'neutral' | 'red' | 'gold' | 'green' | 'blue'; children: ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        tone === 'neutral' && 'bg-white/5 text-zinc-300',
        tone === 'red' && 'bg-blood-600/15 text-blood-400',
        tone === 'gold' && 'bg-gold-400/15 text-gold-300',
        tone === 'green' && 'bg-emerald-500/15 text-emerald-300',
        tone === 'blue' && 'bg-indigo-500/15 text-indigo-300',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-6 w-6 animate-spin text-zinc-500', className)} />;
}

export function PageSpinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {icon && <div className="mb-2 text-4xl">{icon}</div>}
      <p className="font-medium text-zinc-200">{title}</p>
      {children && <div className="text-sm text-zinc-500">{children}</div>}
    </div>
  );
}

export function Alert({ tone = 'red', children }: { tone?: 'red' | 'gold' | 'green'; children: ReactNode }) {
  return (
    <div
      className={clsx(
        'rounded-xl border px-4 py-3 text-sm',
        tone === 'red' && 'border-blood-600/30 bg-blood-600/10 text-blood-400',
        tone === 'gold' && 'border-gold-400/30 bg-gold-400/10 text-gold-300',
        tone === 'green' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
      )}
    >
      {children}
    </div>
  );
}

export function Modal({ open, onClose, title, children }: { open: boolean; onClose(): void; title: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="card max-h-[92dvh] w-full max-w-lg animate-fade-in overflow-y-auto rounded-b-none p-5 sm:rounded-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl">{title}</h2>
          <button className="rounded-lg p-1 text-zinc-500 hover:bg-white/5 hover:text-white" onClick={onClose} aria-label="Закрыть">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useGame();
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-3" aria-live="polite">
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => dismiss(t.id)}
          className={clsx(
            'pointer-events-auto w-full max-w-md animate-fade-in rounded-xl border px-4 py-3 text-left text-sm shadow-2xl backdrop-blur',
            t.tone === 'info' && 'border-white/10 bg-ink-800/95 text-zinc-100',
            t.tone === 'good' && 'border-emerald-500/30 bg-emerald-950/95 text-emerald-200',
            t.tone === 'bad' && 'border-blood-600/40 bg-blood-700/95 text-white',
          )}
        >
          {t.text}
        </button>
      ))}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage(p: number): void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 pt-4 text-sm text-zinc-400">
      <span>
        Стр. {page} из {pages} · всего {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Назад
        </Button>
        <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Вперёд
        </Button>
      </div>
    </div>
  );
}
